import type { CSSProperties, ReactNode } from 'react';

export function CeremonialPanelFrame() {
  return (
    <svg className="ceremonial-panel-frame" viewBox="0 0 900 760" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id="panel-paper" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fffaf8" stopOpacity=".99" />
          <stop offset="1" stopColor="#f9eeee" stopOpacity=".99" />
        </linearGradient>
        <linearGradient id="panel-gold" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#e7c995" />
          <stop offset=".5" stopColor="#b9844d" />
          <stop offset="1" stopColor="#ead4a8" />
        </linearGradient>
        <filter id="panel-shadow" x="-20%" y="-20%" width="140%" height="150%">
          <feDropShadow dx="0" dy="16" stdDeviation="18" floodColor="#68455f" floodOpacity=".24" />
        </filter>
      </defs>
      <path className="panel-paper-shape" filter="url(#panel-shadow)" fill="url(#panel-paper)" d="M58 30C204 24 287 24 363 17c43-4 66-12 87-12s44 8 87 12c76 7 159 7 305 13 29 1 49 22 49 50v625c0 28-21 49-49 49H58c-28 0-49-21-49-49V80c0-28 20-49 49-50Z" />
      <path fill="none" stroke="url(#panel-gold)" strokeWidth="3" d="M64 40c144-5 229-5 304-12 42-4 62-11 82-11s40 7 82 11c75 7 160 7 304 12 24 1 42 18 42 42v619c0 23-18 41-42 41H64c-24 0-42-18-42-41V82c0-24 18-41 42-42Z" />
      <path fill="none" stroke="#ecd6b2" strokeWidth="2" d="M74 54c139-4 225-4 299-11 39-4 58-10 77-10s38 6 77 10c74 7 160 7 299 11 19 1 34 15 34 34v601c0 19-15 34-34 34H74c-19 0-34-15-34-34V88c0-19 15-33 34-34Z" />
      <g className="panel-corner-filigree" fill="none" stroke="#c69d63" strokeWidth="2">
        <path className="panel-filigree-top" d="M28 150c34-3 54-20 60-56 18 26 38 35 66 35M872 150c-34-3-54-20-60-56-18 26-38 35-66 35" />
        <path className="panel-filigree-bottom" d="M28 625c34 3 54 20 60 56 18-26 38-35 66-35M872 625c-34 3-54 20-60 56-18-26-38-35-66-35" />
      </g>
      <g className="panel-flower panel-flower-top" transform="translate(55 116)">
        <ellipse cy="-17" rx="8" ry="18" /><ellipse cx="17" rx="18" ry="8" /><ellipse cy="17" rx="8" ry="18" /><ellipse cx="-17" rx="18" ry="8" /><circle r="6" />
      </g>
      <g className="panel-flower panel-flower-bottom" transform="translate(839 666)">
        <ellipse cy="-17" rx="8" ry="18" /><ellipse cx="17" rx="18" ry="8" /><ellipse cy="17" rx="8" ry="18" /><ellipse cx="-17" rx="18" ry="8" /><circle r="6" />
      </g>
    </svg>
  );
}

export function OrnateControlFrame({ tone = 'paper' }: { tone?: 'paper' | 'rose' }) {
  return (
    <svg className={`ornate-control-frame tone-${tone}`} viewBox="0 0 720 132" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id={`control-${tone}`} x1="0" y1="0" x2="1" y2="1">
          {tone === 'rose' ? (
            <><stop offset="0" stopColor="#d97488" /><stop offset=".5" stopColor="#c85d78" /><stop offset="1" stopColor="#b94d69" /></>
          ) : (
            <><stop offset="0" stopColor="#fffdfb" /><stop offset="1" stopColor="#fbf2f1" /></>
          )}
        </linearGradient>
      </defs>
      <path className="control-fill" fill={`url(#control-${tone})`} d="M62 5h596c22 0 38 12 47 31l11 30-11 30c-9 19-25 31-47 31H62c-22 0-38-12-47-31L4 66l11-30C24 17 40 5 62 5Z" />
      <path className="control-outline" d="M64 12h592c18 0 32 10 39 26l10 28-10 28c-7 16-21 26-39 26H64c-18 0-32-10-39-26L15 66l10-28c7-16 21-26 39-26Z" />
      <path className="control-inner" d="M72 22h576c15 0 26 8 32 21l8 23-8 23c-6 13-17 21-32 21H72c-15 0-26-8-32-21l-8-23 8-23c6-13 17-21 32-21Z" />
      <path className="control-diamond" d="m21 66 11-11 11 11-11 11Zm678 0 11-11 11 11-11 11Z" />
    </svg>
  );
}

export function RibbonFrame() {
  return (
    <svg className="ribbon-frame" viewBox="0 0 760 84" preserveAspectRatio="none" aria-hidden="true">
      <path className="ribbon-fill" d="M56 7h648l18 15 30 20-30 20-18 15H56L38 62 8 42l30-20Z" />
      <path className="ribbon-line" d="M61 14h638l16 13 23 15-23 15-16 13H61L45 57 22 42l23-15Z" />
      <path className="ribbon-spark" d="m49 42 6-6 6 6-6 6Zm662 0 6-6 6 6-6 6Z" />
    </svg>
  );
}

export function BlossomMark({ className = '', style }: { className?: string; style?: CSSProperties }) {
  return (
    <svg className={`blossom-mark ${className}`} style={style} viewBox="0 0 60 60" aria-hidden="true">
      <g transform="translate(30 30)">
        <ellipse cy="-14" rx="8" ry="16" /><ellipse cx="14" rx="16" ry="8" /><ellipse cy="14" rx="8" ry="16" /><ellipse cx="-14" rx="16" ry="8" /><circle r="6" />
      </g>
    </svg>
  );
}

export function AnswerFrame() {
  return (
    <svg className="answer-frame" viewBox="0 0 760 134" preserveAspectRatio="none" aria-hidden="true">
      <path className="answer-fill" d="M38 4h684c20 0 34 14 34 34v58c0 20-14 34-34 34H38C18 130 4 116 4 96V38C4 18 18 4 38 4Z" />
      <path className="answer-outline" d="M43 10h674c18 0 30 12 30 30v54c0 18-12 30-30 30H43c-18 0-30-12-30-30V40c0-18 12-30 30-30Z" />
      <path className="answer-corner" d="M14 42c22-2 34-12 39-31 9 15 20 23 39 26M14 92c22 2 34 12 39 31 9-15 20-23 39-26" />
    </svg>
  );
}

export function AnswerMedallion({ children }: { children: ReactNode }) {
  return (
    <span className="answer-medallion">
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <path d="M50 5c13 0 20 12 23 22 10-3 24-1 28 11 5 13-5 24-15 29 5 10 7 24-4 32-10 8-23 2-32-6-9 8-22 14-32 6-11-8-9-22-4-32C4 62-6 51-1 38c4-12 18-14 28-11C30 17 37 5 50 5Z" />
        <path className="medallion-inner" d="M50 15c10 0 15 10 18 19 9-3 19-1 23 8 4 10-4 18-13 22 5 9 6 19-3 26-8 6-18 1-25-5-7 6-17 11-25 5-9-7-8-17-3-26-9-4-17-12-13-22 4-9 14-11 23-8 3-9 8-19 18-19Z" />
      </svg>
      <b>{children}</b>
    </span>
  );
}
